import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useResetPasswordMutation } from '../redux/features/api';
import { useValidationForm } from './useValidationForm';
import { useRecaptcha } from './useRecaptcha';
import { APP_PATH_LOGIN } from '../config';
import queryString from 'query-string';
import {
  trackResetPasswordError,
  trackResetPasswordPageView,
  trackResetPasswordSubmit,
  trackResetPasswordSuccess
} from '../helpers';

const schema = {
  newPassword: {
    presence: { allowEmpty: false, message: 'is required' },
    length: {
      minimum: 8,
      message: 'must be at least 8 characters'
    }
  },
  confirmPassword: {
    presence: { allowEmpty: false, message: 'is required' },
    equality: {
      attribute: 'newPassword',
      message: 'passwords do not match'
    }
  }
};

export const useResetPassword = () => {
  const history = useHistory();
  const location = useLocation();
  const [resetPassword, { isLoading, isSuccess }] = useResetPasswordMutation();
  const { executeRecaptcha } = useRecaptcha();
  const [token, setToken] = useState('');

  const { formState, handleChange, hasError } = useValidationForm({
    schemaToValidate: schema
  });

  useEffect(() => {
    // Extract token from URL query parameters
    const parsed = queryString.parse(location.search);
    if (parsed.token) {
      setToken(parsed.token);
      // Track page view with valid token
      trackResetPasswordPageView(true);
    } else {
      // Track page view without valid token
      trackResetPasswordPageView(false);
    }
  }, [location.search]);

  useEffect(() => {
    // Redirect to login page after successful password reset
    if (isSuccess) {
      // Track success
      trackResetPasswordSuccess();

      setTimeout(() => {
        history.push(APP_PATH_LOGIN);
      }, 2000);
    }
  }, [isSuccess, history]);

  const handleBack = useCallback(() => {
    history.push(APP_PATH_LOGIN);
  }, [history]);

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      if (!token) {
        console.error('No reset token found');
        trackResetPasswordError('missing_token');
        return;
      }

      try {
        // Track reset password form submission
        trackResetPasswordSubmit();

        // Get reCAPTCHA token
        const recaptchaToken = await executeRecaptcha('reset_password');

        // Call the reset password API
        await resetPassword({
          token,
          newPassword: formState.values.newPassword,
          recaptchaToken
        }).unwrap();
      } catch (error) {
        console.error('Reset password error:', error);

        // Track error with type
        const errorMessage = error?.data?.message || error?.message || '';
        let errorType = 'unknown_error';

        if (errorMessage.includes('expired')) {
          errorType = 'expired_token';
        } else if (errorMessage.includes('invalid')) {
          errorType = 'invalid_token';
        } else if (errorMessage.includes('password')) {
          errorType = 'validation_error';
        }

        trackResetPasswordError(errorType);
      }
    },
    [token, formState.values.newPassword, resetPassword, executeRecaptcha]
  );

  return {
    formState,
    handleChange,
    handleBack,
    handleSubmit,
    hasError,
    isLoading,
    isSuccess,
    token
  };
};
